import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Pencil } from "lucide-react";

const KOMPONEN = ["Struktur", "Atap", "Instalasi listrik", "Pipa dan Air", "Dinding dan Lantai", "Pintu dan Jendela", "Finishing", "Lainnya"];

export default function Garansi() {
  const [units, setUnits] = useState([]);
  const [report, setReport] = useState({ expiring_30d: [], claims_per_component: [] });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ komplain_deadline: "", garansi: [] });

  const load = useCallback(() => {
    api.get("/sales/garansi/units").then((r) => setUnits(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/sales/garansi/report").then((r) => setReport(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (u) => {
    setForm({ komplain_deadline: u.komplain_deadline || "", garansi: (u.garansi || []).map((g) => ({ ...g })) });
    setEdit(u);
  };
  const setG = (i, k, v) => setForm((f) => ({ ...f, garansi: f.garansi.map((g, j) => (j === i ? { ...g, [k]: v } : g)) }));
  const save = () => api.put(`/sales/bookings/${edit.booking_id}/garansi`, form)
    .then(() => { toast.success("Garansi disimpan"); setEdit(null); load(); })
    .catch((e) => toast.error(errMsg(e)));

  return (
    <div data-testid="garansi-page">
      <PageHeader title="Garansi Konsumen" subtitle="Masa garansi per unit serah terima — tersinkron ke aplikasi ABAD JAYA LIVING" testid="garansi-header" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <Card testid="garansi-expiring">
          <p className="text-xs text-slate-400 mb-2">Garansi berakhir ≤ 30 hari</p>
          {report.expiring_30d.length === 0 && <p className="text-sm text-slate-500">Tidak ada.</p>}
          {report.expiring_30d.map((e) => (
            <div key={e.booking_id} className="text-sm flex justify-between py-1 border-b border-slate-800/60 last:border-0">
              <span className="text-slate-300">{e.customer_name} <span className="text-slate-500">({e.unit})</span></span>
              <Badge tone="amber">{e.komplain_deadline}</Badge>
            </div>
          ))}
        </Card>
        <Card testid="garansi-claims">
          <p className="text-xs text-slate-400 mb-2">Jumlah klaim per komponen</p>
          {report.claims_per_component.length === 0 && <p className="text-sm text-slate-500">Belum ada klaim.</p>}
          <div className="flex flex-wrap gap-1.5">
            {report.claims_per_component.map((c) => <Badge key={c.komponen} tone="cyan">{c.komponen}: {c.klaim}</Badge>)}
          </div>
        </Card>
      </div>

      <div className="space-y-2" data-testid="garansi-list">
        {units.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada unit serah terima.</p></Card>}
        {units.map((u) => (
          <Card key={u.booking_id} className="!p-4" testid={`garansi-${u.booking_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="font-medium text-slate-200">{u.customer_name}</span>
              <Badge tone="cyan">{u.unit}</Badge>
              <span className="text-[11px] text-slate-500">Serah terima: {u.tanggal_serah_terima}</span>
              <Badge tone="amber">Garansi s/d {u.komplain_deadline || "-"}</Badge>
              <button onClick={() => openEdit(u)} className="ml-auto text-slate-400 hover:text-emerald-300" data-testid={`garansi-edit-${u.booking_id}`}><Pencil className="w-4 h-4" /></button>
            </div>
            {(u.garansi || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {u.garansi.map((g, i) => <Badge key={i} tone="slate">{g.komponen} s/d {g.berlaku_sampai}</Badge>)}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Garansi — ${edit?.customer_name} (${edit?.unit})`} testid="garansi-modal">
        <Field label="Akhir masa garansi (komplain deadline)">
          <input type="date" className={inputCls} value={form.komplain_deadline} onChange={(e) => setForm({ ...form, komplain_deadline: e.target.value })} data-testid="garansi-deadline" />
        </Field>
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">Rincian garansi per komponen</p>
            <Btn className="!py-1 !px-2.5 text-xs" onClick={() => setForm((f) => ({ ...f, garansi: [...f.garansi, { komponen: "Struktur", berlaku_sampai: "", catatan: "" }] }))} data-testid="garansi-add"><Plus className="w-3.5 h-3.5" /> Tambah</Btn>
          </div>
          {form.garansi.map((g, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_150px_1fr_36px] gap-2 items-center border border-slate-800 rounded-lg p-2">
              <select className={inputCls} value={g.komponen} onChange={(e) => setG(i, "komponen", e.target.value)} data-testid={`garansi-komponen-${i}`}>
                {KOMPONEN.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input type="date" className={inputCls} value={g.berlaku_sampai} onChange={(e) => setG(i, "berlaku_sampai", e.target.value)} data-testid={`garansi-sampai-${i}`} />
              <input className={inputCls} placeholder="Catatan" value={g.catatan} onChange={(e) => setG(i, "catatan", e.target.value)} data-testid={`garansi-catatan-${i}`} />
              <button onClick={() => setForm((f) => ({ ...f, garansi: f.garansi.filter((_, j) => j !== i) }))} className="text-rose-400" data-testid={`garansi-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <Btn onClick={save} className="mt-4" data-testid="garansi-save">Simpan Garansi</Btn>
      </Modal>
    </div>
  );
}
